import _ from 'lodash';
import { adjustForLocalOffset, getCurrentTimeNY, getFormattedDate } from './date-helpers';
import { Institution } from '../models/institution-model';
import { UnitedStatesCountryCode } from '../../pages/arc/constants/constant-strings';
import { PersonalFormValues } from '../../pages/account-info/features/personal-info/personal-form-models';
import { Person } from '../models/person-model';
import { Notification, NotificationItemMap } from '../../pages/notification/notification-models';
import { RegularExpressions } from '../constants/regular-expressions';
import { replaceAll } from './string-helpers';
import { personInfoSelfDescribeValue } from '../validation-schemas/account-information-form-validation';

export const MinDate = new Date(-8630000000000000);
export const GenderIdentityFieldName = 'gender_identity_other';

export function reformatDateString(date: string) {
	if (date?.includes('/')) {
		const array = date?.split('/');
		return `${array[1]}${array[2]}${array[0]}`;
	}
	
	return date;
}

export function formatDate(date, useSlashes?: boolean, mdy?: boolean) {

	const dateString: string = date;
	const isMasked = dateString?.toString().includes('XX');

	const d = new Date(date);
	let month = isMasked ? 'XX' : (d.getMonth() + 1).toString();
	let day = isMasked ? 'XX' : d.getDate().toString();
	const year = isMasked ? dateString.substring(dateString.lastIndexOf('/') + 1) : d.getFullYear().toString();

	if (month.length < 2)
		month = `0${month}`;
	if (day.length < 2)
		day = `0${day}`;

	const divider = useSlashes ? '/' : '-';
	return mdy ? [month, day, year].join(divider) : [year, month, day].join(divider);
}

export function getCurrentFormattedDateNY(mdy?: boolean) {
	return formatDate(getCurrentTimeNY(), true, mdy);
}

export function addDays(date: Date | string, days: number) {
	const result = adjustForLocalOffset(new Date(date));
	result.setDate(result.getDate() + days);

	return result;
}

export function getStartDateOrDefault(compareDate: string, formatOnly: boolean, daysToAdd: number) {
	const adjusted = addDays(compareDate ? new Date(compareDate) : new Date(), formatOnly ? 0 : daysToAdd);

	if (formatOnly || formatDate(adjusted) > formatDate(getCurrentTimeNY())) {
		return getFormattedDate(adjusted.toString());
	}

	return getFormattedDate(new Date().toString());
}

export const getAreaCodeAndNumber = (country: string, formated: string) => {
	if (!country || !formated) {
		return { areaCode: '', phone: '' };
	}

	const trimed = formated.replace('(', '').replace(')', '').replace('-', '').trim();

	if (country === UnitedStatesCountryCode) {
		return { areaCode: trimed.slice(0, 3), phone: trimed.slice(3, trimed.length) };
	}

	return { areaCode: '', phone: trimed };
};

export const parsePhone = (updated: any) => {

	const res = { ...updated };

	if (res.business_phone_formated) {
		const bp = getAreaCodeAndNumber(updated.business_phone_country_code, updated.business_phone_formated);
		res.business_phone_area_code = bp.areaCode;
		res.business_phone_number = bp.phone;
	} else {
		res.business_phone_number = '';
		res.business_phone_extension = '';
		res.business_phone_area_code = '';
		res.business_phone_country_code = '';
	}
	if (res.mobile_phone_formated) {
		const mp = getAreaCodeAndNumber(updated.mobile_phone_country_code, updated.mobile_phone_formated);
		res.mobile_phone_area_code = mp.areaCode;
		res.mobile_phone_number = mp.phone;
	} else {
		res.mobile_phone_number = '';
		res.mobile_phone_extension = '';
		res.mobile_phone_area_code = '';
		res.mobile_phone_country_code = '';
	}
	if (res.home_phone_formated) {
		const hp = getAreaCodeAndNumber(updated.home_phone_country_code, updated.home_phone_formated);
		res.home_phone_area_code = hp.areaCode;
		res.home_phone_number = hp.phone;
	} else {
		res.home_phone_number = '';
		res.home_phone_extension = '';
		res.home_phone_area_code = '';
		res.home_phone_country_code = '';
	}

	if (res.office_phone_formatted) {
		const hp = getAreaCodeAndNumber(updated.office_country_code, updated.office_phone_formatted);
		res.office_phone_area_code = hp.areaCode;
		res.office_phone_number = hp.phone;
	} else {
		res.office_phone_number = '';
		res.office_phone_extension = '';
		res.office_phone_area_code = '';
		res.office_phone_country_code = '';
	}

	if (res.fax_formatted) {
		const hp = getAreaCodeAndNumber(updated.fax_country_code, updated.fax_formatted);
		res.fax_phone_area_code = hp.areaCode;
		res.fax_phone_number = hp.phone;
	} else {
		res.fax_phone_number = '';
		res.fax_phone_extension = '';
		res.fax_phone_area_code = '';
		res.fax_phone_country_code = '';
	}

	delete res.business_phone_formated;
	delete res.home_phone_formated;
	delete res.mobile_phone_formated;
	delete res.office_phone_formatted;
	delete res.fax_phone_formatted;

	return res;
};

export const parseInstitutionPhone = (updated: Institution) => {

	const res = { ...updated };

	if (res.business_phone_formatted) {
		const bp = getAreaCodeAndNumber(updated.business_phone_country_code, updated.business_phone_formatted);
		res.business_phone_area_code = bp.areaCode;
		res.business_phone_number = bp.phone;
	} else {
		res.business_phone_number = '';
		res.business_phone_extension = '';
		res.business_phone_area_code = '';
		res.business_phone_country_code = '';
	}
	if (res.fax_phone_formatted) {
		const mp = getAreaCodeAndNumber(updated.fax_phone_country_code, updated.fax_phone_formatted);
		res.fax_phone_area_code = mp.areaCode;
		res.fax_phone_number = mp.phone;
	} else {
		res.fax_phone_number = '';
		res.fax_phone_extension = '';
		res.fax_phone_area_code = '';
		res.fax_phone_country_code = '';
	}

	delete res.business_phone_formatted;
	delete res.fax_phone_formatted;
	return res;
};

const repopulatedPhone = (country: string, area: string, phone: string, ext: string, parsed: Object, initial: Object) => {
	const res = { ...parsed };

	// if any part of the phone changed
	if (parsed[country] !== undefined || parsed[area] !== undefined || (parsed[phone] !== undefined && parsed[phone]?.replace('-', '') !== initial[phone]?.replace('-', '')) || parsed[ext] !== undefined) {
		res[country] = parsed[country] !== undefined ? parsed[country] : initial[country];
		res[area] = parsed[area] !== undefined ? parsed[area] : initial[area];
		if (res[country] !== UnitedStatesCountryCode) res[area] = '';
		res[phone] = parsed[phone] !== undefined ? parsed[phone] : initial[phone];
		if (ext.includes('business')) res[ext] = parsed[ext] !== undefined ? parsed[ext] : initial[ext];
	} else {
		delete res[area];
		delete res[country];
		delete res[phone];
		delete res[ext];
	}

	return res;

};

// if anything related to address has changed, the API requires all address fields to be added (not just the changed fields).  This method will add the unchanged address fields to the request.
const addAddressRelatedProperties = (initial: Object, updated: Object, parsed: any, addressPrefix: string, personal?: boolean) => {
	const countriesWithStates = ['US', 'MX', 'CA'];

	const updatedParsed = parsed;

	if (Object.keys(updatedParsed).some(key => key.includes(addressPrefix))) {		

		if (updatedParsed.mailing_country && !countriesWithStates.includes(updatedParsed.mailing_country)) {
			updatedParsed.mailing_state = '';
		}

		if (updatedParsed.mailing_country === 'US' && !personal) {
			updatedParsed.mailing_address_3 = '';
		}
	}

	return updatedParsed;
};

export const parseChanged = (initial: Object, updated: Object, personal?: boolean, ignoreKeysDiff?: Set<string>) => {

	let parsed: any = {};

	Object.keys(initial).forEach(a => {
		// only look for changes if key isn't ignored
		if (ignoreKeysDiff && ignoreKeysDiff.has(a)) {
			parsed[a] = updated[a];
		} else if (a.includes('date')) {
			const formattedInitial = formatDate(initial[a]);
			const formattedUpdated = formatDate(updated[a]);
			if (formattedInitial !== formattedUpdated) {
				parsed[a] = formattedUpdated;
			}
		} else if (initial[a] !== updated[a]) {
			parsed[a] = updated[a];
		}

	});

	parsed = addAddressRelatedProperties(initial, updated, parsed, 'mailing_', personal);
	parsed = addAddressRelatedProperties(initial, updated, parsed, 'physical_');
	parsed = addAddressRelatedProperties(initial, updated, parsed, 'billing_');

	parsed = repopulatedPhone('business_phone_country_code',
		'business_phone_area_code',
		'business_phone_number', 'business_phone_extension', parsed, initial);

	parsed = repopulatedPhone('mobile_phone_country_code',
		'mobile_phone_area_code',
		'mobile_phone_number', 'mobile_phone_extension', parsed, initial);
	parsed = repopulatedPhone('home_phone_country_code',
		'home_phone_area_code',
		'home_phone_number', 'home_phone_extension', parsed, initial);

	parsed = repopulatedPhone('fax_phone_country_code',
		'fax_phone_area_code',
		'fax_phone_number', 'fax_phone_extension', parsed, initial);
	if (parsed?.gender_identity && parsed?.gender_identity?.includes(personInfoSelfDescribeValue)) {
		parsed[GenderIdentityFieldName] = updated[GenderIdentityFieldName];
	}
	delete parsed.business_phone_formated;
	delete parsed.home_phone_formated;
	delete parsed.mobile_phone_formated;
	delete parsed.matched_person;
	delete parsed.attestation1;
	delete parsed.attestation2;

	return parsed;
};

export const getChangedAddress = (initial: PersonalFormValues, updated: PersonalFormValues, currentPerson: Person) => {

	const state =
		updated.mailing_country === 'CA' || updated.mailing_country === 'US' || updated.mailing_country === 'MX' ? updated.mailing_state : '';

	return {
		address1: initial.mailing_address_1 !== updated.mailing_address_1 ? updated.mailing_address_1 : currentPerson.mailing_address_1,
		address2: initial.mailing_address_2 !== updated.mailing_address_2 ? updated.mailing_address_2 : currentPerson.mailing_address_2,
		address3: initial.mailing_address_3 !== updated.mailing_address_3 ? updated.mailing_address_3 : currentPerson.mailing_address_3,
		city: initial.mailing_city !== updated.mailing_city ? updated.mailing_city : initial.mailing_city,
		state: initial.mailing_state !== state ? state : initial.mailing_state,
		postalCode: initial.mailing_postal_code !== updated.mailing_postal_code ? updated.mailing_postal_code : initial.mailing_postal_code,
		country: initial.mailing_country !== updated.mailing_country ? updated.mailing_country : initial.mailing_country,
	};
};

export const parseCurrency = (currency: string) => {
	if (!currency) return '0.00';

	return parseFloat(currency.replace(/[^0-9.]/g, '')).toFixed(2);
};

export const isStringEmpty = (object: Object) => {

	if (typeof object === 'string' || object instanceof String) {
		return !object || object.trim() === '';
	}
	return true;
};

export const parseLov = (object: any, ignoreKey?: boolean) => {
	const keys = Object.keys(object);
	return keys.map(a => { return { value: ignoreKey ? object[a] : a, text: object[a] }; });
};

export function combineDesignationSalution(designation: string, salutation: string) {
	return designation || salutation
		? `${designation || salutation}|${salutation || ''}`
		: '';
}

export const appendDocument = (object: any, documents: any[]) => {
	const copy = _.cloneDeep(object);
	copy.documents = documents;
	return copy;
};

export const formatPhone = (phoneNumber: string, countryCode: string) => {

	if (!phoneNumber || !countryCode) {
		return undefined;
	}

	let formattedPhone = phoneNumber.trim();
	if (countryCode === UnitedStatesCountryCode) {
		formattedPhone = `${formattedPhone.slice(0, 3)}-${formattedPhone.slice(3, 7)}`;
	}

	return formattedPhone;
};

function ExtractPlaceHolders(message: string) : string[]{
	if (!message) return null;

	return message.match(RegularExpressions.PlaceholderPattern);
}

function PersonalizeMessage(message: string, placeholder: string, customFields: any) : string{
	if (!placeholder) return message;	
	if (!message) return null;

	const placeholderName = replaceAll(replaceAll(placeholder, '}', ''), '{', '');

	const personalValue = customFields && customFields[placeholderName] ? customFields[placeholderName] : ' ';

	return replaceAll(message, placeholder, personalValue);
}

export function MapNotification(notification: Notification, notificationMapping: NotificationItemMap[]) : Notification {
	if (notificationMapping?.length > 0){		
		
		const cmsNotification = notificationMapping.find(n => n.notificationRefId?.toLocaleLowerCase() === notification.notification_ref_id?.toLocaleLowerCase());

		if (cmsNotification){

			const placeholdersSubject = ExtractPlaceHolders(cmsNotification.subject);
			const placeholdersBody = ExtractPlaceHolders(cmsNotification.body);

			let personalSubject = cmsNotification.subject;

			if (personalSubject && placeholdersSubject?.length > 0){
				placeholdersSubject?.forEach(placeholder => {
					personalSubject = PersonalizeMessage(personalSubject, placeholder, notification.custom_fields);
				});
			}

			let personalBody = cmsNotification.body;

			if (personalSubject && placeholdersBody?.length > 0){
				placeholdersBody?.forEach(placeholder => {
					personalBody = PersonalizeMessage(personalBody, placeholder, notification.custom_fields);
				});
			}

			return {
				...notification,
				notification_subject: personalSubject || notification.notification_subject,
				notification_body: personalBody || notification.notification_body,
				is_mandatory: cmsNotification.mandatory || false,
			};
		}				
		
		return notification;			
	}

	return notification;
}

export const formatFormatedPhoneNumber = (phoneNumber: string) => {
	const digitsOnly = phoneNumber.replace(/\D/g, '');
	return digitsOnly.length === 10
	  ? `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`
	  : phoneNumber;
  };

/**
 * Returns date in the format MM - MMMM dd, yyyy.
 * @param date Date to format.
 * @returns Formated date.
 */
export function formatDateMonthNumber(date: Date) {

    const monthLong = date.toLocaleString('en-US', { month: 'long' });
    const month = date.getMonth();
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month + 1} - ${monthLong} ${day}, ${year}`;

}